import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { Card } from '../../core/Card/Card.js';
import { Text } from '../../core/Text/Text.js';
import { FitText } from '../../core/FitText/FitText.js';
import { Badge } from '../../core/Badge/Badge.js';
import { Group } from '../../core/Group/Group.js';
import { Stack } from '../../core/Stack/Stack.js';
import { Sparkline } from '../../charts/Sparkline.js';
import './MetricCard.css';

export interface MetricCardProps extends Omit<BaseProps, 'color'> {
  /** Short label above the value. */
  label: ReactNode;
  /** The main value (already formatted as a string or React node). */
  value: ReactNode;
  /** Tiny icon shown next to the label. */
  icon?: ReactNode;
  /** Percentage change vs previous period. Positive = green, negative = red. */
  trend?: number | null;
  /** Override the trend label (defaults to `±N%`). */
  trendLabel?: ReactNode;
  /** Sub-text under the value. */
  hint?: ReactNode;
  /** Optional sparkline series (numbers). Renders as a full-width strip at the bottom. */
  sparkline?: number[] | null;
  /** When true, renders a "(demo)" pill in the corner. */
  demo?: boolean;
  /** Sparkline strip height in px. Default 36. */
  sparklineHeight?: number;
  /** Loading skeleton. */
  loading?: boolean;
}

function fmtTrend(n: number): string {
  if (n === 0) return '0%';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

/**
 * KPI card. Label → big value → hint → trend → full-width sparkline strip.
 * The strip-style layout reads cleanly at any card width, where the legacy
 * side-by-side sparkline used to squeeze multi-word hints into vertical
 * one-word-per-line columns.
 */
export const MetricCard = forwardRef(function MetricCard(
  {
    label, value, icon, trend, trendLabel, hint, sparkline, demo,
    sparklineHeight = 36, loading, ...rest
  }: MetricCardProps,
  ref: Ref<HTMLElement>
): ReactNode {
  const trendColor = trend == null ? 'gray' : trend > 0 ? 'green' : trend < 0 ? 'red' : 'gray';
  const trendNegative = trend != null && trend < 0;

  return (
    <Card ref={ref} withBorder className="rr-metric-card" {...rest}>
      <Stack gap="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            {icon ? <span className="rr-metric-card__icon" aria-hidden>{icon}</span> : null}
            <Text role="overline" className="rr-metric-card__label" truncate>
              {label}
            </Text>
          </Group>
          {demo ? <Badge size="xs" variant="outline" color="gray">demo</Badge> : null}
        </Group>

        <Stack gap={4}>
          {loading ? (
            <span className="rr-metric-card__skeleton" />
          ) : (
            // The figure shrinks to its card rather than wrapping or losing its
            // last digits under an ellipsis: "R 1 234 567,89" is one line.
            <Text role="headline" fw={700} className="rr-metric-card__value"><FitText>{value}</FitText></Text>
          )}
          {hint ? <Text role="bodySmall" className="rr-metric-card__hint">{hint}</Text> : null}
          {trend != null && (
            <Group gap="xs" align="center">
              <Badge size="xs" variant="light" color={trendColor} className="rr-metric-card__trend">
                {trendLabel ?? fmtTrend(trend)}
              </Badge>
              <Text role="caption" className="rr-metric-card__trend-label">vs previous period</Text>
            </Group>
          )}
        </Stack>

        {sparkline && sparkline.length > 1 && (
          <div className="rr-metric-card__spark" style={{ height: sparklineHeight }}>
            <Sparkline
              data={sparkline}
              color={trendNegative ? 'var(--rr-color-error)' : 'var(--rr-color-primary)'}
              h={sparklineHeight}
              w="100%"
              withArea
            />
          </div>
        )}
      </Stack>
    </Card>
  );
});
